import { useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { ChevronLeft, Bed, Users, Clock, Calendar as CalendarIcon, Shield, Mail, CheckCircle2 } from 'lucide-react';
import { ShareButton } from '@/components/share/ShareButton';
import { producerPublicPath } from '@/lib/paths';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useCoverImageUrl, useGalleryUrls, useMediaAssets } from '@/hooks/useMediaAssets';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import { HeroCarousel, type HeroImage } from '@/components/place/HeroCarousel';
import { PlaceCoreGalleryStrip } from '@/components/placecore/public/PlaceCoreGalleryStrip';
import { RichTextRenderer } from '@/components/common/RichTextRenderer';
import { StickyBottomBar } from '@/components/ui/StickyBottomBar';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import {
  parsePriceTextToOre,
  formatDisplayPriceLabel,
  hasSeasonalPricing,
  type PriceRule,
} from '@/lib/accommodation/pricing';
import {
  AccommodationBookingForm,
  resolveBookingMode,
  useBookingModeCopy,
} from '@/components/accommodation/AccommodationBookingForm';
import {
  resolvePrepaymentPolicy,
  getPrepaymentExpectationText,
  getPrepaymentExpectationShort,
  getPrepaymentSuccessText,
} from '@/lib/booking/prepayment';

interface AccommodationPublic {
  id: string;
  title: string;
  description_short: string | null;
  description: string | null;
  beds: number | null;
  guest_capacity: number | null;
  price_per_night_text: string | null;
  check_in_time: string | null;
  check_out_time: string | null;
  house_rules: string | null;
  producer_id: string;
  booking_deadline_hours: number;
  booking_mode: string;
  prepayment_policy_default: string | null;
}

interface SuccessData {
  email: string;
  checkIn: Date;
  checkOut: Date;
  nights: number;
  totalKr: number | null;
}

export default function AccommodationDetailPage() {
  const { accommodationId } = useParams<{ accommodationId: string }>();
  const isMobile = useIsMobile();

  const [bookingOpen, setBookingOpen] = useState(false);
  const [success, setSuccess] = useState<SuccessData | null>(null);

  const { data: accommodation, isLoading } = useQuery({
    queryKey: ['public-accommodation', accommodationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('producer_accommodations')
        .select('id, title, description_short, description, beds, guest_capacity, price_per_night_text, check_in_time, check_out_time, house_rules, producer_id, booking_deadline_hours, booking_mode, prepayment_policy_default')
        .eq('id', accommodationId!)
        .eq('status', 'public')
        .single();
      if (error) throw error;
      return data as AccommodationPublic;
    },
    enabled: !!accommodationId,
  });

  const { data: producer } = useQuery({
    queryKey: ['accommodation-producer', accommodation?.producer_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('producers')
        .select('id, name, slug')
        .eq('id', accommodation!.producer_id)
        .single();
      if (error) throw error;
      return data as { id: string; name: string; slug: string };
    },
    enabled: !!accommodation?.producer_id,
  });

  const { data: blocks = [] } = useQuery({
    queryKey: ['public-accommodation-blocks', accommodationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accommodation_availability_blocks')
        .select('start_date, end_date')
        .eq('accommodation_id', accommodationId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!accommodationId,
  });

  const { data: priceRules = [] } = useQuery({
    queryKey: ['public-accommodation-price-rules', accommodationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accommodation_price_rules')
        .select('id, rule_type, title, start_date, end_date, price_ore, is_active')
        .eq('accommodation_id', accommodationId!)
        .eq('is_active', true);
      if (error) throw error;
      return (data ?? []) as PriceRule[];
    },
    enabled: !!accommodationId,
  });

  const coverUrl = useCoverImageUrl('accommodation', accommodationId, 1280);
  const galleryUrls = useGalleryUrls('accommodation', accommodationId, 640);
  const galleryThumbUrls = useGalleryUrls('accommodation', accommodationId, 320);

  const { data: coverAssets } = useMediaAssets('accommodation', accommodationId, 'cover', null);
  const { data: galleryAssets } = useMediaAssets('accommodation', accommodationId, 'gallery', null);
  const [heroIndex, setHeroIndex] = useState(0);

  const heroImages: HeroImage[] = useMemo(() => {
    const imgs: HeroImage[] = [];
    if (coverAssets?.length) {
      const sorted = [...coverAssets].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      const url = resolveAssetUrl(sorted[0], 1280);
      if (url) imgs.push({ id: sorted[0].id, src: url, alt: accommodation?.title ?? 'Omslagsbild' });
    }
    if (galleryAssets?.length) {
      for (const a of galleryAssets) {
        const url = resolveAssetUrl(a, 1280);
        if (url) imgs.push({ id: a.id, src: url, alt: accommodation?.title ?? 'Bild' });
      }
    }
    return imgs;
  }, [coverAssets, galleryAssets, accommodation?.title]);

  const stripThumbUrls = useMemo(() => {
    const urls: string[] = [];
    if (coverUrl) urls.push(coverUrl.replace('/1280/', '/320/'));
    urls.push(...galleryThumbUrls);
    return urls;
  }, [coverUrl, galleryThumbUrls]);

  const stripFullUrls = useMemo(() => {
    const urls: string[] = [];
    if (coverUrl) urls.push(coverUrl.replace('/1280/', '/640/'));
    urls.push(...galleryUrls);
    return urls;
  }, [coverUrl, galleryUrls]);

  const handleHeroIndexChange = useCallback((index: number) => {
    setHeroIndex(index);
  }, []);

  const bookingMode = resolveBookingMode(accommodation?.booking_mode);
  const modeCopy = useBookingModeCopy(bookingMode);
  const prepaymentPolicy = resolvePrepaymentPolicy(accommodation?.prepayment_policy_default);
  const basePriceOre = accommodation ? parsePriceTextToOre(accommodation.price_per_night_text) : null;
  const priceLabel = formatDisplayPriceLabel(basePriceOre, priceRules);
  const priceLabelFinal = priceLabel ?? (() => {
    const text = accommodation?.price_per_night_text;
    if (!text) return null;
    const trimmed = text.trim();
    if (!trimmed) return null;
    if (/kr|per|natt/i.test(trimmed)) return trimmed;
    if (/^\d+$/.test(trimmed)) return `Från ${trimmed} kr per natt`;
    return trimmed;
  })();
  const hasSeasonal = hasSeasonalPricing(priceRules);

  const handleBookingSuccess = (formData: SuccessData) => {
    setBookingOpen(false);
    setSuccess(formData);
  };

  if (isLoading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-muted-foreground">Laddar...</div>;
  }

  if (!accommodation) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-2">Boendet hittades inte</h1>
        <p className="text-muted-foreground">Det här boendet finns inte eller är inte tillgängligt.</p>
      </div>
    );
  }

  // --- Success state ---
  if (success) {
    const accommodationName = accommodation.title || 'boendet';
    const primaryLabel = accommodationName.length <= 30
      ? `Tillbaka till ${accommodationName}`
      : 'Tillbaka till boendet';

    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 sm:p-10 space-y-6">
          {/* Icon + heading */}
          <div className="space-y-3">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" aria-hidden="true" />
            <h1 className="text-2xl font-bold text-foreground">
              {bookingMode === 'optional' ? 'Bokning bekräftad!' : 'Förfrågan skickad!'}
            </h1>
            <p className="text-foreground/70 text-sm leading-relaxed max-w-sm mx-auto">
              {bookingMode === 'optional'
                ? <>Din bokning är bekräftad. En bekräftelse har skickats till <strong className="text-foreground">{success.email}</strong>.</>
                : <>Din förfrågan har skickats till värden. Du får en bekräftelse till <strong className="text-foreground">{success.email}</strong>.</>
              }
            </p>
          </div>

          {/* Stay summary card */}
          <div className="bg-background border border-border rounded-lg px-5 py-4 text-left space-y-2">
            <p className="font-semibold text-foreground">{accommodation.title}</p>
            <p className="text-sm text-muted-foreground">
              {format(success.checkIn, 'd MMM', { locale: sv })} – {format(success.checkOut, 'd MMM yyyy', { locale: sv })} · {success.nights} {success.nights === 1 ? 'natt' : 'nätter'}
            </p>
            {success.totalKr != null && (
              <p className="text-base font-semibold text-foreground">{success.totalKr.toLocaleString('sv-SE')} kr totalt</p>
            )}
          </div>

          {/* Trust / status signals */}
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center justify-center gap-2">
              <Shield className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{getPrepaymentSuccessText(prepaymentPolicy)}</span>
            </div>
            {bookingMode === 'required' && (
              <div className="flex items-center justify-center gap-2">
                <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>Värden svarar vanligtvis inom 24 timmar</span>
              </div>
            )}
          </div>

          <Separator />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
            <Button asChild size="default">
              <Link to={`/accommodation/${accommodation.id}`} onClick={() => setSuccess(null)}>
                {primaryLabel}
              </Link>
            </Button>
            {producer && (
              <Button asChild variant="outline" size="default">
                <Link to={producerPublicPath(producer.slug || producer.id)}>
                  Till {producer.name}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- Fact chips ---
  const factChips: { icon: typeof Bed; label: string }[] = [];
  if (accommodation.beds) factChips.push({ icon: Bed, label: `${accommodation.beds} bäddar` });
  if (accommodation.guest_capacity) factChips.push({ icon: Users, label: `Max ${accommodation.guest_capacity} gäster` });
  if (accommodation.check_in_time) factChips.push({ icon: Clock, label: `Incheckning ${accommodation.check_in_time}` });
  if (accommodation.check_out_time) factChips.push({ icon: Clock, label: `Utcheckning ${accommodation.check_out_time}` });

  const stickyPriceText = priceLabelFinal ?? 'Se tillgänglighet';

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6 pb-28 md:pb-8">
        {/* Back link */}
        {producer && (
          <Link
            to={producerPublicPath(producer.slug || producer.id)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors -mb-2"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Till {producer.name}</span>
          </Link>
        )}

        {/* Hero carousel */}
        {heroImages.length > 0 && (
          <div className="relative aspect-[4/3] sm:aspect-[16/9] rounded-none sm:rounded-xl overflow-hidden bg-muted -mx-4 sm:mx-0">
            <HeroCarousel
              images={heroImages}
              className="absolute inset-0 w-full h-full"
              onIndexChange={handleHeroIndexChange}
              activeIndex={heroIndex}
            />
          </div>
        )}

        {/* Gallery strip */}
        {stripThumbUrls.length > 1 && (
          <PlaceCoreGalleryStrip
            thumbUrls={stripThumbUrls}
            fullUrls={stripFullUrls}
            activeHeroIndex={isMobile ? heroIndex : undefined}
            onSelectIndex={isMobile ? (idx) => setHeroIndex(idx) : undefined}
          />
        )}

        {/* Header + facts + price */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-3xl font-bold text-foreground">{accommodation.title}</h1>
            <ShareButton
              kind="accommodation"
              slug={accommodation.id}
              title={accommodation.title}
              size="default"
              className="shrink-0 mt-1"
            />
          </div>

          {producer && (
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground">
                Hos <Link to={producerPublicPath(producer.slug || producer.id)} className="underline underline-offset-2 hover:text-foreground transition-colors">{producer.name}</Link>
              </p>
            </div>
          )}

          {factChips.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {factChips.map((chip, i) => (
                <Badge key={i} variant="secondary" className="font-normal gap-1.5 py-1 px-2.5">
                  <chip.icon className="h-3.5 w-3.5" />
                  {chip.label}
                </Badge>
              ))}
            </div>
          )}

          {priceLabelFinal ? (
            <div>
              <p className="text-lg font-semibold text-foreground">{priceLabelFinal}</p>
              {hasSeasonal && (
                <p className="text-xs text-muted-foreground mt-0.5">Priset varierar över säsong</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">Pris visas vid förfrågan</p>
          )}
        </div>

        {/* ── Booking entry card ── */}
        <Card className="border-primary/20 bg-primary/[0.02]">
          <CardContent className="py-5 px-5">
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-0.5">
                <p className="font-semibold text-foreground">{modeCopy.heading}</p>
                <p className="text-sm text-muted-foreground">{modeCopy.subtitle}</p>
              </div>
              <Button onClick={() => setBookingOpen(true)} size="lg" className="shrink-0">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Välj datum
              </Button>
            </div>
            {/* Trust signals inline */}
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" />
                {getPrepaymentExpectationText(prepaymentPolicy)}
              </span>
              {modeCopy.trustLine && (
                <span className="flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" />
                  {modeCopy.trustLine}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Short description */}
        {accommodation.description_short && (
          <p className="text-foreground/80 leading-relaxed">{accommodation.description_short}</p>
        )}

        {/* Full description (rich text) */}
        {accommodation.description && (
          <div className="prose prose-sm max-w-none">
            <RichTextRenderer html={accommodation.description} />
          </div>
        )}

        {accommodation.house_rules && (
          <div>
            <h3 className="font-medium mb-1">Husregler</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{accommodation.house_rules}</p>
          </div>
        )}
      </div>

      {/* ── Booking overlay (responsive: bottom sheet on mobile, dialog on desktop) ── */}
      <ResponsiveOverlay
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        title={modeCopy.heading}
        desktopVariant="dialog"
        desktopMaxWidth="2xl"
      >
        <AccommodationBookingForm
          accommodationId={accommodation.id}
          accommodationTitle={accommodation.title}
          bookingMode={bookingMode}
          guestCapacity={accommodation.guest_capacity}
          pricePerNightText={accommodation.price_per_night_text}
          bookingDeadlineHours={accommodation.booking_deadline_hours ?? 24}
          priceRules={priceRules}
          blocks={blocks}
          prepaymentPolicy={prepaymentPolicy}
          onSuccess={handleBookingSuccess}
        />
      </ResponsiveOverlay>

      {/* ── Mobile sticky action bar ── */}
      <StickyBottomBar bottomOffsetPx={0}>
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">{stickyPriceText}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Shield className="h-3 w-3" />
              {getPrepaymentExpectationShort(prepaymentPolicy)}
            </span>
          </div>
          <Button
            onClick={() => setBookingOpen(true)}
            className="w-full"
            size="lg"
          >
            {modeCopy.ctaShort}
          </Button>
        </div>
      </StickyBottomBar>
    </>
  );
}
